import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Extract YouTube ID from any share/watch URL. Returns null if not a YouTube URL.
const parseYouTubeId = (url) => {
  if (!url || typeof url !== "string") return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([\w-]{6,})/,
    /youtube\.com\/shorts\/([\w-]{6,})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
};

export default function Hero() {
  const [slides, setSlides] = useState([]);
  const [idx, setIdx] = useState(0);
  const [prevIdx, setPrevIdx] = useState(-1);
  const [direction, setDirection] = useState(1); // 1 = next, -1 = prev
  const timerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/public/sliders`).then((r) => setSlides(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (slides.length <= 1) return;
    timerRef.current = setInterval(() => {
      setPrevIdx((curr) => curr);
      setDirection(1);
      setIdx((i) => (i + 1) % slides.length);
    }, 6500);
    return () => clearInterval(timerRef.current);
  }, [slides.length]);

  const go = (n) => {
    setPrevIdx(idx);
    setDirection(n);
    setIdx((idx + n + slides.length) % slides.length);
  };

  const jumpTo = (i) => {
    if (i === idx) return;
    setPrevIdx(idx);
    setDirection(i > idx ? 1 : -1);
    setIdx(i);
  };

  const display = slides.length ? slides : [{
    id: "placeholder",
    title: "Building reliable medicines for a healthier tomorrow",
    subtitle: "Research-driven formulations, regulatory-grade manufacturing, and a portfolio that serves clinicians across geographies.",
    media_type: "image",
    media_url: null,
  }];

  const current = display[idx];
  const currentIsVideo =
    !!parseYouTubeId(current?.media_url) ||
    (current?.media_type === "video" && current?.media_url);

  return (
    <section className="relative w-full bg-[#0F172A] overflow-hidden" data-testid="hero">
      <div className="relative w-full h-[88vh] min-h-[520px] max-h-[820px]">
        {display.map((s, i) => {
          const isCurrent = i === idx;
          const wasPrev = i === prevIdx;
          const ytId = (s.media_type === "youtube" || s.media_type === "video") ? parseYouTubeId(s.media_url) : null;
          const mediaSrc = s.media_url && s.media_url.startsWith("http") ? s.media_url : s.media_url ? `${BACKEND}${s.media_url}` : null;
          // Slide-in from right (next) or left (prev) via clip-path
          const clipIn = direction >= 0
            ? "polygon(0 0, 100% 0, 100% 100%, 0 100%)"
            : "polygon(0 0, 100% 0, 100% 100%, 0 100%)";
          return (
            <div
              key={s.id}
              className={`absolute inset-0 transition-all duration-[1100ms] ease-[cubic-bezier(0.65,0,0.35,1)] ${isCurrent ? "opacity-100 z-[2]" : wasPrev ? "opacity-100 z-[1]" : "opacity-0 z-0 pointer-events-none"}`}
              style={isCurrent ? {
                clipPath: clipIn,
                animation: `${direction >= 0 ? "heroSlideInRight" : "heroSlideInLeft"} 1.1s cubic-bezier(0.65,0,0.35,1) both`,
              } : {}}
              aria-hidden={!isCurrent}
            >
              {ytId ? (
                <div className="absolute inset-0 overflow-hidden">
                  {/* Scale the iframe up + center it so YouTube's title bar and
                      end-screen thumbnails fall outside the visible viewport,
                      leaving a clean, background-video-style playback. */}
                  <iframe
                    src={`https://www.youtube.com/embed/${ytId}?autoplay=1&mute=1&loop=1&controls=0&showinfo=0&modestbranding=1&rel=0&iv_load_policy=3&disablekb=1&playsinline=1&playlist=${ytId}`}
                    title={s.title || "Hero video"}
                    className="pointer-events-none absolute top-1/2 left-1/2"
                    style={{
                      border: 0,
                      width: "177.78vw",             // 16:9 based on viewport height
                      height: "56.25vw",             // 16:9 based on viewport width
                      minWidth: "100%",
                      minHeight: "100%",
                      transform: "translate(-50%, -50%) scale(1.35)",
                    }}
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen={false}
                    loading={i === 0 ? "eager" : "lazy"}
                  />
                </div>
              ) : s.media_type === "video" && mediaSrc ? (
                <video src={mediaSrc} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
              ) : mediaSrc ? (
                <img
                  src={mediaSrc}
                  alt={s.title || ""}
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ animation: isCurrent ? "kenBurns 8s ease-out both" : "none" }}
                  loading={i === 0 ? "eager" : "lazy"}
                />
              ) : (
                <div
                  aria-hidden
                  className="absolute inset-0 w-full h-full"
                  style={{
                    background:
                      "radial-gradient(120% 100% at 0% 0%, #C2410C22 0%, transparent 55%), radial-gradient(120% 100% at 100% 100%, #0EA5E933 0%, transparent 55%), linear-gradient(135deg, #0F172A 0%, #1E293B 100%)",
                    animation: isCurrent ? "kenBurns 8s ease-out both" : "none",
                  }}
                />
              )}
              {/* Overlay gradients — dim images so hero text stays readable,
                  but lighten dramatically for YouTube/video so the footage
                  stays bright. Keep a tiny bottom fade so the title copy
                  still separates from the moving frames. */}
              {ytId || (s.media_type === "video" && mediaSrc) ? (
                <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#0F172A]/70 via-[#0F172A]/20 to-transparent pointer-events-none" />
              ) : (
                <>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/55 to-[#0F172A]/10" />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/55 via-transparent to-transparent" />
                </>
              )}
            </div>
          );
        })}

        {/* Editorial molecule mesh — slowly rotating abstract chemical bond
            drawn in sheer saffron + sky blue. Hidden on video slides so
            footage stays clean and unobstructed. */}
        {!currentIsVideo && (
        <svg
          className="absolute right-[-10%] top-[10%] w-[70vw] max-w-[900px] opacity-30 pointer-events-none z-[3]"
          viewBox="0 0 400 400"
          aria-hidden
          data-testid="hero-molecule"
          style={{ animation: "moleculeSpin 90s linear infinite" }}
        >
          <g fill="none" strokeWidth="1" stroke="#0EA5E9" opacity="0.9">
            <circle cx="200" cy="200" r="60" />
            <circle cx="200" cy="200" r="120" />
            <circle cx="200" cy="200" r="180" />
            <line x1="20"  y1="200" x2="380" y2="200" />
            <line x1="200" y1="20"  x2="200" y2="380" />
            <line x1="60"  y1="60"  x2="340" y2="340" />
            <line x1="340" y1="60"  x2="60"  y2="340" />
          </g>
          <g fill="#C2410C" stroke="#C2410C" strokeWidth="1">
            <circle cx="200" cy="200" r="6" />
            <circle cx="200" cy="80"  r="4" />
            <circle cx="200" cy="320" r="4" />
            <circle cx="80"  cy="200" r="4" />
            <circle cx="320" cy="200" r="4" />
            <circle cx="130" cy="130" r="3" />
            <circle cx="270" cy="270" r="3" />
            <circle cx="270" cy="130" r="3" />
            <circle cx="130" cy="270" r="3" />
          </g>
        </svg>
        )}

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-16 lg:pb-24 text-white z-10">
          <div className="max-w-2xl">
            <div
              key={`badge-${idx}`}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-[10px] tracking-[0.3em] uppercase font-semibold mb-5"
              style={{ animation: "blurUp 0.8s ease-out both" }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#FB923C] animate-pulse" /> Bonheur Pharmaceuticals
            </div>
            <h1
              key={`title-${idx}`}
              className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light tracking-tighter leading-[1.02]"
              style={{ animation: "blurUp 0.9s ease-out 0.15s both" }}
            >
              {current.title || "Building reliable medicines for a healthier tomorrow"}
            </h1>
            {current.subtitle && (
              <p
                key={`sub-${idx}`}
                className="mt-3 text-sm sm:text-base text-white/80 max-w-xl leading-relaxed"
                style={{ animation: "blurUp 0.9s ease-out 0.3s both" }}
              >
                {current.subtitle}
              </p>
            )}
            {current.cta_label && current.link_url && (
              <a
                key={`cta-${idx}`}
                href={current.link_url}
                className="btn-fluid mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white text-white text-sm font-semibold"
                style={{ animation: "blurUp 0.9s ease-out 0.45s both" }}
              >
                {current.cta_label}
              </a>
            )}
          </div>
        </div>

        {display.length > 1 && (
          <>
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20" data-testid="hero-dots">
              {display.map((_, i) => (
                <button key={i} onClick={() => jumpTo(i)} aria-label={`Slide ${i + 1}`}
                  className={`h-1 rounded-full transition-all duration-300 ${i === idx ? "w-10 bg-[#FB923C]" : "w-4 bg-white/40 hover:bg-white/70"}`} />
              ))}
            </div>
            <button onClick={() => go(-1)} data-testid="hero-prev" className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur-sm items-center justify-center text-white transition-colors duration-200 z-20" aria-label="Previous">
              <CaretLeft size={18} weight="bold" />
            </button>
            <button onClick={() => go(1)} data-testid="hero-next" className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur-sm items-center justify-center text-white transition-colors duration-200 z-20" aria-label="Next">
              <CaretRight size={18} weight="bold" />
            </button>
          </>
        )}
      </div>
    </section>
  );
}
